import { LinearProgress } from '@mui/material'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { I18n, Translate } from 'src/i18n'
import styled from 'styled-components'
import { defaultTheme } from 'src/theme'
import { useListsStore } from 'src/store/lists/ListsStore'
import { ListGroupPartRow } from './ListGroupPartRow'
import { BottomDrawer } from './BottomDrawer'
import { CartEditItemDrawer } from './CartEditItemDrawer'
import { MoveGroupPartsDrawer } from './drawers/MoveGroupPartsDrawer'
import { MovePartsSelectionDrawer } from './MovePartsSelectionDrawer'
import { breakpoints } from 'src/theme/breakpoints'
import { ListSortDropdown } from './ListSortDropdown'
import { KeyboardArrowUp } from '@mui/icons-material'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import { GroupPart } from 'src/store/lists/interfaces'
import Pager from 'src/ui-components/table/pagination/'
import EmptyGroupScreen from '../EmptyGroupScreen'
import AddMenu from './AddMenu'
import SelectPartsMenu from './SelectPartsMenu'
import { SelectionOption } from './interfaces'

const listModulePages = [
  {
    value: '10',
    label: '10',
  },
  {
    value: '25',
    label: '25',
  },
  {
    value: '50',
    label: '50',
  },
  {
    value: '100',
    label: '100',
  },
]

export const ListGroupPartsTable = observer((): React.ReactElement => {
  const listStore = useListsStore()
  const {
    groupPartsLoading,
    setShowBottomDrawer,
    handlePaginationLimit,
    getThumbnailUrl,
    currentPage,
    itemsPerPage,
    selectedCategory,
    selectedList,
    selectedGroup,
    selectionOption,
    selectedGroupParts,
  } = listStore

  const sortButtonDesktopRef = useRef<HTMLDivElement>(null)
  const [showSortBoxDesktop, setShowSortBoxDesktop] = useState(false)

  const [partsToShow, setPartsToShow] = useState<GroupPart[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const totalPartsSelected = listStore.getAllSelectedParts()?.length || 0

  const currentSelection = useMemo<SelectionOption>(() => {
    if (totalPartsSelected <= 0) return SelectionOption.NONE

    const selectedParts =
      listStore
        .getSelectedPartsBasket()
        ?.items?.find((l) => l.listId === selectedList.id)
        ?.groups?.find((g) => g.id === selectedGroup?.id)?.parts || []

    const selectedPartsIds = new Set(selectedParts.map((s) => s.id))

    const allSelected = (parts: GroupPart[]) =>
      parts.every((p) => selectedPartsIds.has(p.id))

    const groupParts = selectedGroupParts
    const areAllSelected = allSelected(groupParts)

    if (areAllSelected) {
      return selectionOption.get(selectedGroup.id) === SelectionOption.FULL_PAGE
        ? SelectionOption.FULL_PAGE
        : SelectionOption.FULL_GROUP
    }

    const end = currentPage + itemsPerPage
    const pageParts = listStore.getGroupSlice(currentPage, end)

    const areAllpagePartsSelected = allSelected(pageParts)
    if (areAllpagePartsSelected) return SelectionOption.FULL_PAGE

    return selectionOption.get(selectedGroup?.id) || SelectionOption.NONE
  }, [
    totalPartsSelected,
    listStore,
    currentPage,
    itemsPerPage,
    selectionOption,
    selectedGroup,
    selectedList,
    selectedGroupParts,
  ])

  const partsCount = listStore.selectedGroupParts?.length ?? 0

  useEffect(() => setLoading(groupPartsLoading), [groupPartsLoading])

  const getPageSlice = useCallback(
    (start, end): void => {
      setLoading(true)
      const parts = listStore.getGroupSlice(start, end)
      setPartsToShow(parts)
      setLoading(false)
    },
    [listStore]
  )

  useEffect(() => {
    if (groupPartsLoading) return
    const end = currentPage + itemsPerPage
    getPageSlice(currentPage, end)
  }, [
    currentPage,
    itemsPerPage,
    groupPartsLoading,
    listStore.selectedGroupParts,
    getPageSlice,
  ])

  const isGroupEmpty = useMemo(() => partsCount <= 0, [partsCount])

  const handlePageChange = (page: number, currentPageSize: number) => {
    const start = (page - 1) * currentPageSize // -1 to start counting from 0
    handlePaginationLimit(start, currentPageSize)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const selectPageItems = () => {
    const end = currentPage + itemsPerPage
    const listId = selectedList.id
    const groupId = selectedGroup.id
    listStore.selectItems(selectedCategory, listId, groupId, currentPage, end)

    const newMap = new Map(selectionOption)
    newMap.set(groupId, SelectionOption.FULL_PAGE)
    listStore.setSelectionOption(newMap)

    setShowBottomDrawer(true)
  }

  const selectGroupItems = () => {
    const listId = selectedList.id
    const groupId = selectedGroup.id
    listStore.selectItems(selectedCategory, listId, groupId)

    const newMap = new Map(selectionOption)
    newMap.set(groupId, SelectionOption.FULL_GROUP)
    listStore.setSelectionOption(newMap)

    setShowBottomDrawer(true)
  }

  const clearAllSelections = () => {
    listStore.clearAllSelections()
    listStore.setSelectionOption(new Map())
    setShowBottomDrawer(false)
  }

  const selectPart = (part: GroupPart) => {
    const listId = selectedList.id
    const groupId = selectedGroup.id
    listStore.selectItem(selectedCategory, listId, groupId, part)

    const newMap = new Map(selectionOption)
    newMap.set(groupId, SelectionOption.CUSTOM)
    listStore.setSelectionOption(newMap)

    const atleastOnePartSelected = listStore.getAllSelectedParts()?.length > 0
    setShowBottomDrawer(atleastOnePartSelected)
  }

  const isSelected = (part: GroupPart): boolean => {
    const listId = listStore.getSelectedListId()
    const groupId = listStore.selectedGroup?.id
    return listStore.isPartSelected(listId, groupId, part)
  }

  const showClearSelections =
    listStore
      ?.getSelectedPartsBasket()
      ?.items?.find((l) => l.listId === selectedList.id)
      ?.groups.find((g) => g.id === selectedGroup.id)?.parts?.length > 0

  const ListHeader = (
    <ListHeaderDesktopView>
      <PartCountSection>
        <Title>
          <I18n name="group" />
        </Title>
        {`${partsCount} ${Translate('parts')}`}
      </PartCountSection>
      <Flex>
        <HeaderSegment>
          <ActionWrapper
            onClick={() => setShowSortBoxDesktop(!showSortBoxDesktop)}
            ref={sortButtonDesktopRef}
          >
            <ActionText>
              <I18n name="sortGroup" />
            </ActionText>
            {showSortBoxDesktop ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </ActionWrapper>
          <ListSortDropdown
            parentRef={sortButtonDesktopRef}
            show={showSortBoxDesktop}
            setShow={() => setShowSortBoxDesktop(false)}
          />
        </HeaderSegment>

        <AddMenu />
      </Flex>
    </ListHeaderDesktopView>
  )

  const tableHeaderRow = (
    <THead>
      <Row>
        <TCell
          style={{
            width: '80px',
          }}
        >
          <SelectPartsMenu
            selectionOption={currentSelection}
            pageItems={partsToShow}
            groupItemsCount={partsCount}
            selectPageItems={selectPageItems}
            selectGroupItems={selectGroupItems}
            showClearSelections={showClearSelections}
            clearSelections={clearAllSelections}
          />
        </TCell>
        <TCell
          style={{
            width: '100px',
          }}
        >
          <TitleContainer>
            <I18n name="brand" />
          </TitleContainer>
        </TCell>
        <TCell
          style={{
            width: '80px',
          }}
        >
          <TitleContainer>
            <I18n name="lineCode" />
          </TitleContainer>
        </TCell>
        <TCell style={{ width: '120px' }}>
          <TitleContainer>
            <I18n name="partNum" />
          </TitleContainer>
        </TCell>
        <TCell style={{ width: '150px' }}>
          <TitleContainer>
            <I18n name="description" />
          </TitleContainer>
        </TCell>
        <TCell style={{ width: '70px' }}>
          <TitleContainer>
            <I18n name="qty" />
          </TitleContainer>
        </TCell>
        <TCell style={{ width: '164px' }}>
          <TitleContainer>
            <I18n name="comments" />
          </TitleContainer>
        </TCell>
        <TCell style={{ width: '90px' }}>
          <TitleContainer>
            <I18n name="dateAdded" />
          </TitleContainer>
        </TCell>
        <TCell style={{ width: '90px' }}>
          <TitleContainer>
            <I18n name="lastUsed" />
          </TitleContainer>
        </TCell>
        <TCell style={{ width: '72px' }} />
      </Row>
    </THead>
  )

  return (
    <Container>
      {loading && <LinearProgress />}
      {!loading && isGroupEmpty && <EmptyGroupScreen />}
      {!isGroupEmpty && (
        <TableContainer>
          <>
            {ListHeader}
            <Table>
              {tableHeaderRow}
              <tbody>
                {partsToShow?.map((part, idx) => (
                  <ListGroupPartRow
                    part={part}
                    key={`group_part_${part.id}_${idx}`}
                    isSelected={isSelected(part)}
                    thumbnail={getThumbnailUrl(
                      part?.partNumber,
                      part?.lineCode
                    )}
                    onChange={selectPart}
                  />
                ))}
              </tbody>
            </Table>
            <Footer>
              <Pager
                totalItems={partsCount}
                itemsPerPageScale={itemsPerPage}
                currentPageSize={itemsPerPage}
                onPageChange={handlePageChange}
                paginationTitle="products"
                pageOptions={listModulePages}
                filterByPage
              />
            </Footer>
            <MoveGroupPartsDrawer />
            <MovePartsSelectionDrawer />
            <CartEditItemDrawer />
          </>
        </TableContainer>
      )}
      <BottomDrawer clearSelections={clearAllSelections} />
    </Container>
  )
})

const Container = styled.div`
  width: 100%;
  background-color: #ffffff;
`
const TableContainer = styled.div`
  width: 100%;
  background-color: #ffffff;
  overflow-x: hidden;
`

const Table = styled.table`
  width: 100%;
  table-layout: fixed;
`
const THead = styled.thead`
  background: ${defaultTheme.colors.rowFeaturedBackground};
  border-top: 1px solid ${defaultTheme.colors.borderTable};
  width: 100%;
  height: 50px;
  @media screen and (max-width: ${breakpoints.tabletXLarge}px) {
    display: none;
  }
`

interface TCellProps {
  width?: string
}

const TCell = styled.td<TCellProps>`
  text-align: left;
  width: ${(props) => (props.width ? props.width : 'auto')};
`

const Row = styled.tr`
  width: 100%;
  box-shadow: inset 0px -1px 0px ${defaultTheme.colors.greyMedium};
`
const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  text-transform: uppercase;
  font-family: Roboto;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  color: ${defaultTheme.colors.columnBorder};
`

const Footer = styled.div`
  margin: 20px;
`
const ListHeaderDesktopView = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    border-bottom: 1px solid ${defaultTheme.colors.border};
  }
`
const ActionWrapper = styled.div`
  display: flex;
  height: 100%;
  justify-content: center;
  align-items: center;
  border-left: 1px solid ${defaultTheme.colors.border};
  border-right: 1px solid ${defaultTheme.colors.border};
  padding-left: 24px;
  padding-right: 24px;
  padding-top: 12px;
  padding-bottom: 12px;
  cursor: pointer;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    padding: 12px;
  }
`

const ActionText = styled.span`
  font-family: Oswald, serif;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
  text-transform: uppercase;
  padding: 5px;
  color: #000000;
  user-select: none;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    text-transform: capitalize;
    font-family: Roboto;
    font-size: 14px;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0px;
    text-align: left;
  }
`

const HeaderSegment = styled.div`
  height: 100%;
`

const PartCountSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: none;
  }
`
const Title = styled.div`
  font-size: 18px;
  font-family: Oswald, serif;
  font-weight: bold;
  padding: 12px 12px 12px 24px;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    padding: 12px 12px 12px 0px;
  }
`

const Flex = styled.div`
  display: flex;
`
